import React, { useState } from 'react';
import { TextField, InputAdornment, IconButton, Menu, MenuItem, Typography } from '@mui/material';

const CustomTimePicker = ({
    label,
    value,
    onChange,
    name,
    placeholder = 'Select time',
    format = '12h', // '12h' or '24h'
    disabled = false,
    error
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const openTimePicker = (event) => {
        if (disabled) return;
        setAnchorEl(event.currentTarget);
    };

    const closeTimePicker = () => {
        setAnchorEl(null);
    };

    const handleTimeSelect = (time) => {
        onChange({ target: { name, value: time } });
        closeTimePicker();
    };

    // Format display text
    const getDisplayText = () => {
        if (!value) return '';

        if (format === '12h') {
            const [hours, minutes] = value.split(':');
            const hour = parseInt(hours, 10);
            const minute = parseInt(minutes, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;
            const formattedMinute = minute.toString().padStart(2, '0');
            return `${formattedHour}:${formattedMinute} ${ampm}`;
        }

        return value;
    };

    // Generate time options
    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) { // 30-minute intervals
                const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                times.push(time24);
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    return (
        <>
            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: 'text.main', mb: 0.8 }}>
                {label}
            </Typography>
            <div style={{ position: 'relative', width: '100%' }}>
                <TextField
                    fullWidth
                    type="time"
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    size="small"
                    disabled={disabled}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: '40px',
                            backgroundColor: disabled ? 'rgba(0, 0, 0, 0.04)' : 'white',
                            '& input': {
                                padding: '8px 12px',
                                fontSize: '14px',
                                opacity: 0,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                '&::-webkit-calendar-picker-indicator': {
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '100%',
                                    height: '100%',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: 0,
                                },
                            },
                            '& fieldset': {
                                border: `1px solid ${disabled ? 'rgba(0, 0, 0, 0.12)' : 'lightgray'} !important`,
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${disabled ? 'rgba(0, 0, 0, 0.12)' : 'lightgray'} !important`,
                            },
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" sx={{ zIndex: 2 }}>
                                <IconButton
                                    size="small"
                                    sx={{
                                        mr: -0.5,
                                        zIndex: 3,
                                        '&:hover': {
                                            backgroundColor: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    onClick={openTimePicker}
                                    disabled={disabled}
                                >
                                    <img
                                        src='/Images/Calendar.png'
                                        style={{
                                            width: 20,
                                            position: 'relative'
                                        }}
                                        alt=''
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <div
                    onClick={disabled ? undefined : openTimePicker}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '12px',
                        paddingRight: '40px',
                        fontSize: '14px',
                        color: value ? 'inherit' : '#6B7280',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        backgroundColor: 'transparent',
                        pointerEvents: 'none',
                        zIndex: 1,
                        opacity: disabled ? 0.5 : 1,
                    }}
                >
                    {getDisplayText() || placeholder}
                </div>
            </div>

            {/* Custom Time Dropdown */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={closeTimePicker}
                PaperProps={{
                    sx: {
                        maxHeight: 300,
                        width: 150,
                    }
                }}
            >
                {timeOptions.map((time) => (
                    <MenuItem
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        selected={value === time}
                        sx={{ fontSize: '14px' }}
                    >
                        {format === '12h' ?
                            (() => {
                                const [hours, minutes] = time.split(':');
                                const hour = parseInt(hours, 10);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const formattedHour = hour % 12 || 12;
                                return `${formattedHour}:${minutes} ${ampm}`;
                            })()
                            : time
                        }
                    </MenuItem>
                ))}
            </Menu>
            {error && (
                <Typography variant="caption" color="error" sx={{ fontSize: '12px', display: 'block' }}>
                    {error}
                </Typography>
            )}
        </>
    );
};

export default CustomTimePicker;
